import React from "react";
import { Typography, Box } from "@mui/material";
import { apiDateToDate, formatEventDateLocal } from "../utils/dateConverters";
import linkDisplayLabel from "../utils/linkDisplayLabel";
import CategoryTagsCell from "./CategoryTagsCell";

export const SECTION_TITLE_PROPS = { fontWeight: 600, mt: 2, mb: 0.5 };

export function EventDetailContent({ data, onOpenDetail, DateIRLToErache }) {
  if (!data) return null;
  const date = data.date ? apiDateToDate(data.date) : null;
  return (
    <Box sx={{ px: 1 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
        {data.name || "—"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Type : Événement
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        Date IRL : {date ? formatEventDateLocal(data.date) : "—"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Erache : {date ? DateIRLToErache(date) : "—"}
      </Typography>

      {data.periodsUsingEvent && data.periodsUsingEvent.length > 0 && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Périodes qui utilisent cet événement</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {data.periodsUsingEvent.map((p) => (
              <li key={p.id}>
                <Typography
                  component="button"
                  variant="body2"
                  onClick={() => onOpenDetail && onOpenDetail({ type: "period", id: p.id })}
                  sx={{
                    background: "none",
                    border: "none",
                    cursor: onOpenDetail ? "pointer" : "default",
                    color: "primary.main",
                    textAlign: "left",
                    p: 0,
                  }}
                >
                  {p.name}
                </Typography>
              </li>
            ))}
          </Box>
        </>
      )}

      {data.rpDescription && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Description RP</Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {data.rpDescription}
          </Typography>
        </>
      )}
      {data.hrpDescription && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Description HRP</Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {data.hrpDescription}
          </Typography>
        </>
      )}
      {data.categories && data.categories.length > 0 && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Catégories</Typography>
          <CategoryTagsCell categories={data.categories} />
        </>
      )}
      {data.links && data.links.length > 0 && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Liens</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {data.links.map((url, i) => (
              <li key={i}>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {linkDisplayLabel(url)}
                </a>
              </li>
            ))}
          </Box>
        </>
      )}
      {/* TODO: List of timelines that use/reference this event (timelines not implemented yet) */}
      <Typography {...SECTION_TITLE_PROPS}>Frises chronologiques</Typography>
      <Typography variant="body2" color="text.secondary">
        (À venir)
      </Typography>

      {data.authorPseudo && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Créé par</Typography>
          <Typography variant="body2">{data.authorPseudo}</Typography>
        </>
      )}
      {data.voteCount !== undefined && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Votes</Typography>
          <Typography variant="body2">{data.voteCount ?? 0}</Typography>
        </>
      )}
    </Box>
  );
}

export function PeriodDetailContent({ data, onOpenDetail, DateIRLToErache }) {
  if (!data) return null;
  const startDate = data.startDate ? apiDateToDate(data.startDate) : null;
  const endDate = data.endDate ? apiDateToDate(data.endDate) : null;
  return (
    <Box sx={{ px: 1 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
        {data.name || "—"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Type : Période
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        Début IRL : {data.startDate ? formatEventDateLocal(data.startDate) : "—"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        Début Erache : {startDate ? DateIRLToErache(startDate) : "—"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        Fin IRL : {data.endDate ? formatEventDateLocal(data.endDate) : "En cours"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Fin Erache : {endDate ? DateIRLToErache(endDate) : "En cours"}
      </Typography>

      <Typography {...SECTION_TITLE_PROPS}>Événements de la période</Typography>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        <li>
          <Typography
            component="button"
            variant="body2"
            onClick={() => data.startEventId && onOpenDetail && onOpenDetail({ type: "event", id: data.startEventId })}
            sx={{
              background: "none",
              border: "none",
              cursor: data.startEventId && onOpenDetail ? "pointer" : "default",
              color: data.startEventId && onOpenDetail ? "primary.main" : "text.primary",
              textAlign: "left",
              p: 0,
            }}
          >
            Départ : {data.startEventName || "—"}
          </Typography>
        </li>
        <li>
          <Typography
            component="button"
            variant="body2"
            onClick={() => data.endEventId && onOpenDetail && onOpenDetail({ type: "event", id: data.endEventId })}
            sx={{
              background: "none",
              border: "none",
              cursor: data.endEventId && onOpenDetail ? "pointer" : "default",
              color: data.endEventId && onOpenDetail ? "primary.main" : "text.primary",
              textAlign: "left",
              p: 0,
            }}
          >
            Fin : {data.endEventName || "En cours"}
          </Typography>
        </li>
      </Box>

      {data.rpDescription && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Description RP</Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {data.rpDescription}
          </Typography>
        </>
      )}
      {data.hrpDescription && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Description HRP</Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {data.hrpDescription}
          </Typography>
        </>
      )}
      {data.categories && data.categories.length > 0 && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Catégories</Typography>
          <CategoryTagsCell categories={data.categories} />
        </>
      )}
      {data.links && data.links.length > 0 && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Liens</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {data.links.map((url, i) => (
              <li key={i}>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {linkDisplayLabel(url)}
                </a>
              </li>
            ))}
          </Box>
        </>
      )}
      {/* TODO: List of timelines that use/reference this period (timelines not implemented yet) */}
      <Typography {...SECTION_TITLE_PROPS}>Frises chronologiques</Typography>
      <Typography variant="body2" color="text.secondary">
        (À venir)
      </Typography>

      {data.authorPseudo && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Créé par</Typography>
          <Typography variant="body2">{data.authorPseudo}</Typography>
        </>
      )}
      {data.voteCount !== undefined && (
        <>
          <Typography {...SECTION_TITLE_PROPS}>Votes</Typography>
          <Typography variant="body2">{data.voteCount ?? 0}</Typography>
        </>
      )}
    </Box>
  );
}
